import * as dayjs from 'dayjs'
import * as Sentry from '@sentry/vue'
import store from '@/store'
import { logInfo, logWarn } from '@/3rd-parties/sentry'
import Router from '../router'

const propertyMappingTable = {
  reactions: 'r',
  audienceId: 'd',
  audienceName: 'e',
  audienceTeam: 'm',
  audienceEmail: 'l',
  audienceEmoji: 'i',
  resetTimeStamp: 'p',
  answeredSlideIds: 's',
  alreadyClearedDataSlides: 'a',
  visited: 'v',
}

const clearValueByPresentation = (presentationId) => {
  try {
    const key = `aha-presentation-${presentationId}`

    localStorage.setItem(key, '{}')
  } catch (error) {
    Sentry.captureException(error)
    Router.app.$router.push({ query: {} })
  }
}

const hookSentryDebugMissAudienceId = (presentationId, property, defaultValue, data) => {
  try {
    if (property === 'audienceId' && store.state.audienceName && !store.state.audienceId) {
      Sentry.captureException(new Error('Debug: cannot load audienceId'), {
        extra: {
          presentationId,
          property,
          defaultValue,
          data,
          audienceName: store.state.audienceName,
        },
      })
    }
  } catch (error) {
    // do nothing
  }
}

const GLOBAL_KEY = 'aha_participant'

const getParticipantGlobalValue = (property, defaultValue = null) => {
  try {
    const jsonData = localStorage.getItem(GLOBAL_KEY) || '{}'
    const data = JSON.parse(jsonData)
    if (Object.prototype.hasOwnProperty.call(data, property)) {
      return data[property]
    }
    return defaultValue
  } catch (error) {
    Sentry.captureException(new Error(`getParticipantGlobalValue: error when getting global value(${property}, ${defaultValue})`))
    return defaultValue
  }
}

const setParticipantGlobalValue = (property, value) => {
  try {
    const jsonData = localStorage.getItem(GLOBAL_KEY) || '{}'
    const data = JSON.parse(jsonData)
    data[property] = value
    localStorage.setItem(GLOBAL_KEY, JSON.stringify(data))
    return value
  } catch (error) {
    Sentry.captureException(new Error(`setParticipantGlobalValue: error when setting global value(${property}, ${value})`))
    return null
  }
}

const getValueByPresentation = (presentationId, property, defaultValue) => {
  const mappedProperty = propertyMappingTable[property] || property
  try {
    const key = `aha-presentation-${presentationId}`
    const jsonData = localStorage.getItem(key) || '{}'
    const data = JSON.parse(jsonData)

    // hook debug
    hookSentryDebugMissAudienceId(presentationId, property, defaultValue, data)

    // clear local storage after 3 days
    if (dayjs().diff(dayjs.unix(data.updatedAt), 'days') >= 3) {
      clearValueByPresentation(presentationId)
      logWarn('clear value in getValueByPresentation after 3 days', { presentationId, ...data })
    } else if (data[mappedProperty]) {
      return data[mappedProperty]
    }
  } catch (error) {
    const value = Router.app.$route.query[mappedProperty]

    if (value) return JSON.parse(value)
  }

  if (defaultValue !== undefined) return defaultValue

  return null
}

const setValueByPresentation = (presentationId, property, value) => {
  const mappedProperty = propertyMappingTable[property] || property

  try {
    const key = `aha-presentation-${presentationId}`
    const jsonData = localStorage.getItem(key) || '{}'
    const data = JSON.parse(jsonData)

    data[mappedProperty] = value
    data.updatedAt = dayjs().unix()

    localStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    Sentry.captureException(error)
    const query = { ...Router.app.$route.query }

    query[mappedProperty] = JSON.stringify(value)
    Router.app.$router.push({ query })
  }
}

const updateValueByPresentation = (presentationId, property, value) => {
  const oldValue = getValueByPresentation(presentationId, property)

  if (oldValue !== value) {
    setValueByPresentation(presentationId, property, value)
    if (property === 'audienceId') {
      logInfo('setting audienceId', { value, oldValue })
    }
  }
}

export default {
  setValueByPresentation,
  getValueByPresentation,
  clearValueByPresentation,
  updateValueByPresentation,
  setParticipantGlobalValue,
  getParticipantGlobalValue,
}
